import {
  createSettingMetadataFromGroupDefaults,
  type SettingMetadataDefaultsContext,
} from "../../settings-catalog/metadata";
import type {
  SettingAgentEditability,
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  InternationalMarketsEditorFlatTeamSettingGroupById,
  type InternationalMarketsEditorFlatTeamSettingGroupId,
} from "./groups";

interface InternationalMarketsEditorSettingInput extends SettingRegistryMetadataInput {}

interface InternationalMarketsEditorSettingRegistryEntry extends InternationalMarketsEditorSettingInput {
  group: InternationalMarketsEditorFlatTeamSettingGroupId;
}

function setting(
  metadata: InternationalMarketsEditorSettingInput,
): InternationalMarketsEditorSettingInput {
  return metadata;
}

function internationalMarketsEditorGroup<
  const Group extends InternationalMarketsEditorFlatTeamSettingGroupId,
  const T extends Record<string, InternationalMarketsEditorSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

const InternationalMarketsEditorFlatSettingCatalogRegistry = {
  ...internationalMarketsEditorGroup(
    "international_markets_editor.market_editor",
    {
      "internationalMarketsEditor/marketEditor/pricingMode": setting({
        label: "Pricing mode",
        description:
          "How this market builds its published prices: markup only, a landed cost calculated per product from its HS code, or a rate Zonos guarantees for the whole country.",
        synonyms: [
          "pricing mode",
          "guaranteed rate",
          "adaptive coefficient",
          "landed cost pricing mode",
          "how prices are calculated",
        ],
        risk: {
          level: "high",
          reason:
            "Changes what shoppers are charged and republishes the market's catalog prices.",
        },
      }),
    },
  ),
};

export type InternationalMarketsEditorFlatTeamSettingKey =
  keyof typeof InternationalMarketsEditorFlatSettingCatalogRegistry;

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const InternationalMarketsEditorFlatTeamSettingKeys = Object.keys(
  InternationalMarketsEditorFlatSettingCatalogRegistry,
) as InternationalMarketsEditorFlatTeamSettingKey[];

const InternationalMarketsEditorFlatTeamSettingKeySet = new Set<string>(
  InternationalMarketsEditorFlatTeamSettingKeys,
);

export function isInternationalMarketsEditorFlatTeamSettingKey(
  key: string,
): key is InternationalMarketsEditorFlatTeamSettingKey {
  return InternationalMarketsEditorFlatTeamSettingKeySet.has(key);
}

function createInternationalMarketsEditorSettingAgentEditability({
  setting,
  group,
  access,
  risk,
  affectsBilling,
}: SettingMetadataDefaultsContext<InternationalMarketsEditorSettingRegistryEntry>): SettingAgentEditability {
  if (setting.agentEditability) {
    return setting.agentEditability;
  }
  if (access.write.mode === "none") {
    return { mode: "never", reason: access.write.reason };
  }
  if (affectsBilling || risk.level === "high" || risk.level === "critical") {
    return {
      mode: "suggestOnly",
      reason: "High-risk settings should be reviewed before saving.",
    };
  }
  return group.agentEditability;
}

export const InternationalMarketsEditorFlatTeamSettingCatalogDefinitions =
  InternationalMarketsEditorFlatTeamSettingKeys.map((key) => {
    const definition =
      InternationalMarketsEditorFlatSettingCatalogRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group:
          InternationalMarketsEditorFlatTeamSettingGroupById[definition.group],
        resolveAgentEditability:
          createInternationalMarketsEditorSettingAgentEditability,
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    InternationalMarketsEditorFlatTeamSettingKey,
    InternationalMarketsEditorFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<InternationalMarketsEditorFlatTeamSettingKey>
  >[];

export const InternationalMarketsEditorFlatTeamSettingMetadataByKey =
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  Object.fromEntries(
    InternationalMarketsEditorFlatTeamSettingCatalogDefinitions.map(
      (definition) => [definition.id, definition.metadata],
    ),
  ) as Record<InternationalMarketsEditorFlatTeamSettingKey, SettingMetadata>;
